// Pure 2D geometric constraint solver (GCS) for the sketch editor. No THREE,
// no DOM — runnable in plain Node so it can be unit-tested like sketch-core.js.
//
// Model (plain data, owned by the caller):
//   sketch = {
//     points: [ { id, x, y, fixed? } ],          // stable ids; fixed removes DOF
//     constraints: [ { id, type, edges?, points?, value? } ],
//   }
// An "edge" in a constraint is an ordered pair of point ids [aId, bId] (its
// direction is b−a). Constraints reference points/edges by id, so they survive
// vertex insertion/deletion (drop any whose ids no longer exist).
//
// Constraint types (residual = 0 when satisfied):
//   horizontal   edges:[e]        a.y − b.y
//   vertical     edges:[e]        a.x − b.x
//   distance     edges:[e] value  |b−a| − value
//   angle        edges:[e] value  b−a aligned to `value`° (mod 180)
//   parallel     edges:[e1,e2]    cross(d1,d2)
//   perpendicular edges:[e1,e2]   dot(d1,d2)
//   equal        edges:[e1,e2]    |d1| − |d2|
//   collinear    edges:[e1,e2]    e2's endpoints on e1's line (2 rows)
//   pointOnLine  points:[p] edges:[e]  p on e's line
//   symmetric    points:[p1,p2] edges:[e]  p1,p2 mirror across e (2 rows)
//   coincident   points:[p1,p2]   p1 − p2 (2 rows)
//   hDistance    edges:[e] value  (b−a).x − value      (horizontal dimension)
//   vDistance    edges:[e] value  (b−a).y − value      (vertical dimension)
//   lockX        points:[p] value p.x − value          (absolute-x lock)
//   lockY        points:[p] value p.y − value          (absolute-y lock)
//   midpoint     points:[m,a,b]   m − (a+b)/2 (2 rows) (m is the midpoint of a→b)
//
// solveSketch() runs Levenberg–Marquardt on the stacked residual vector and
// reports degrees-of-freedom diagnostics. A "soft drag" pull (opts.drag) biases
// the dragged point toward the cursor without overriding the hard constraints.

// ---- small dense linear algebra -------------------------------------------

const dot = (a, b) => { let s = 0; for (let i = 0; i < a.length; i++) s += a[i] * b[i]; return s; };

// Deterministic PRNG (mulberry32) — used only to break residual saddle points
// with reproducible nudges, so the same sketch always solves to the same result
// (no frame-to-frame jitter while dragging).
function mulberry32(seed) {
  let a = seed >>> 0;
  return () => {
    a |= 0; a = (a + 0x6d2b79f5) | 0;
    let t = Math.imul(a ^ (a >>> 15), 1 | a);
    t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t;
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

// Solve the SPD-ish system M·x = b (M is n×n, given as array of Float64Array
// rows) by Gaussian elimination with partial pivoting. Returns null if singular.
function solveLinear(M, b) {
  const n = b.length;
  const A = M.map((row) => Float64Array.from(row));
  const x = Float64Array.from(b);
  for (let col = 0; col < n; col++) {
    let piv = col, best = Math.abs(A[col][col]);
    for (let r = col + 1; r < n; r++) { const v = Math.abs(A[r][col]); if (v > best) { best = v; piv = r; } }
    if (best < 1e-14) return null;
    if (piv !== col) { const t = A[piv]; A[piv] = A[col]; A[col] = t; const tb = x[piv]; x[piv] = x[col]; x[col] = tb; }
    const d = A[col][col];
    for (let r = 0; r < n; r++) {
      if (r === col) continue;
      const f = A[r][col] / d;
      if (f === 0) continue;
      for (let c = col; c < n; c++) A[r][c] -= f * A[col][c];
      x[r] -= f * x[col];
    }
  }
  for (let i = 0; i < n; i++) x[i] /= A[i][i];
  return x;
}

// Rank of an m×n matrix (row echelon with a relative tolerance).
function matrixRank(J, tol = 1e-7) {
  const m = J.length; if (!m) return 0;
  const n = J[0].length;
  const A = J.map((row) => Float64Array.from(row));
  let rank = 0;
  const rows = A.slice();
  for (let col = 0; col < n && rank < m; col++) {
    let piv = -1, best = tol;
    for (let r = rank; r < m; r++) { const v = Math.abs(rows[r][col]); if (v > best) { best = v; piv = r; } }
    if (piv < 0) continue;
    const t = rows[rank]; rows[rank] = rows[piv]; rows[piv] = t;
    const d = rows[rank][col];
    for (let r = 0; r < m; r++) {
      if (r === rank) continue;
      const f = rows[r][col] / d;
      if (f === 0) continue;
      for (let c = col; c < n; c++) rows[r][c] -= f * rows[rank][c];
    }
    rank++;
  }
  return rank;
}

// ---- parameter mapping ----------------------------------------------------

// Map non-fixed points to a flat parameter vector [x0,y0,x1,y1,…].
function buildParamIndex(points) {
  const idx = new Map();
  let n = 0;
  for (const p of points) {
    if (p.fixed) { idx.set(p.id, null); continue; }
    idx.set(p.id, { xi: n, yi: n + 1 });
    n += 2;
  }
  return { idx, n };
}

// A coord accessor over the current parameter vector (fixed points read stored).
function coordGetter(byId, idx, params) {
  return (id) => {
    const m = idx.get(id);
    if (!m) { const p = byId.get(id); return { x: p.x, y: p.y }; }
    return { x: params[m.xi], y: params[m.yi] };
  };
}

// ---- residuals ------------------------------------------------------------

// Residuals are NORMALISED so every type has a comparable scale — distances in
// mm, angular ones dimensionless. Cross/dot products are divided by the edge
// length(s) (a directed line's unit-normal projection = a true mm distance;
// cross/dot of unit directions = sin/cos of the angle). Without this, quadratic
// residuals (mm²) dwarf linear ones and the solver finds degenerate collapses.
const EPS = 1e-6;
function pushConstraintResiduals(out, c, P) {
  const E = (e) => {
    const p0 = P(e[0]), p1 = P(e[1]); const dx = p1.x - p0.x, dy = p1.y - p0.y;
    return { p0, p1, dx, dy, L: Math.hypot(dx, dy) + EPS };
  };
  switch (c.type) {
    case 'horizontal': { const e = E(c.edges[0]); out.push(e.p0.y - e.p1.y); break; }
    case 'vertical': { const e = E(c.edges[0]); out.push(e.p0.x - e.p1.x); break; }
    case 'distance': { const e = E(c.edges[0]); out.push(Math.hypot(e.dx, e.dy) - c.value); break; }
    case 'angle': { const e = E(c.edges[0]); const t = c.value * Math.PI / 180; out.push((e.dy * Math.cos(t) - e.dx * Math.sin(t)) / e.L); break; }
    case 'parallel': { const a = E(c.edges[0]), b = E(c.edges[1]); out.push((a.dx * b.dy - a.dy * b.dx) / (a.L * b.L)); break; }
    case 'perpendicular': { const a = E(c.edges[0]), b = E(c.edges[1]); out.push((a.dx * b.dx + a.dy * b.dy) / (a.L * b.L)); break; }
    case 'equal': { const a = E(c.edges[0]), b = E(c.edges[1]); out.push(Math.hypot(a.dx, a.dy) - Math.hypot(b.dx, b.dy)); break; }
    case 'collinear': {
      const a = E(c.edges[0]); const q0 = P(c.edges[1][0]), q1 = P(c.edges[1][1]);
      out.push((a.dx * (q0.y - a.p0.y) - a.dy * (q0.x - a.p0.x)) / a.L);
      out.push((a.dx * (q1.y - a.p0.y) - a.dy * (q1.x - a.p0.x)) / a.L);
      break;
    }
    case 'pointOnLine': { const e = E(c.edges[0]); const p = P(c.points[0]); out.push((e.dx * (p.y - e.p0.y) - e.dy * (p.x - e.p0.x)) / e.L); break; }
    case 'symmetric': {
      const e = E(c.edges[0]); const A = P(c.points[0]), B = P(c.points[1]);
      const mx = (A.x + B.x) / 2, my = (A.y + B.y) / 2;
      out.push((e.dx * (my - e.p0.y) - e.dy * (mx - e.p0.x)) / e.L); // midpoint on the mirror line (mm)
      out.push(((B.x - A.x) * e.dx + (B.y - A.y) * e.dy) / e.L);     // A→B ⟂ the mirror line (mm)
      break;
    }
    case 'coincident': { const A = P(c.points[0]), B = P(c.points[1]); out.push(A.x - B.x); out.push(A.y - B.y); break; }
    // Horizontal / vertical dimension between two points (signed component of the
    // separation): b−a's x (resp. y) must equal `value`.
    case 'hDistance': { const e = E(c.edges[0]); out.push(e.dx - c.value); break; }
    case 'vDistance': { const e = E(c.edges[0]); out.push(e.dy - c.value); break; }
    // Lock a single coordinate of a point to an absolute value (absolute-position
    // constraint: removes a rigid-body translation freedom).
    case 'lockX': { const p = P(c.points[0]); out.push(p.x - c.value); break; }
    case 'lockY': { const p = P(c.points[0]); out.push(p.y - c.value); break; }
    // Midpoint: point m (points[0]) is the midpoint of segment a→b (points[1,2]).
    case 'midpoint': {
      const M = P(c.points[0]), A = P(c.points[1]), B = P(c.points[2]);
      out.push(M.x - (A.x + B.x) / 2); out.push(M.y - (A.y + B.y) / 2);
      break;
    }
    // Angle at a vertex: the signed angle from ray (vertex→prev) to ray
    // (vertex→next) must equal `value` (degrees). points = [prev, vertex, next].
    // Residual = wrapped (measured − target) in (−π,π]: a UNIQUE zero at the
    // target (unlike sin, which also vanishes at target±180° and would let the
    // corner settle on its mirror). The wrap boundary sits exactly at the
    // reflected corner, so it repels that spurious solution instead of accepting
    // it. Signed target carries the winding, so the corner keeps its turn side.
    case 'angleBetween': {
      const O = P(c.points[1]), A = P(c.points[0]), B = P(c.points[2]);
      const ax = A.x - O.x, ay = A.y - O.y, bx = B.x - O.x, by = B.y - O.y;
      const cross = ax * by - ay * bx, dot = ax * bx + ay * by;
      let d = Math.atan2(cross, dot) - c.value * Math.PI / 180;
      while (d > Math.PI) d -= 2 * Math.PI;
      while (d < -Math.PI) d += 2 * Math.PI;
      out.push(d);
      break;
    }
    // Signed perpendicular distance of point p (points[0]) from edge e's line =
    // `value` (mm, + on the left of a→b).
    case 'pointLineDistance': {
      const e = E(c.edges[0]); const p = P(c.points[0]);
      out.push((e.dx * (p.y - e.p0.y) - e.dy * (p.x - e.p0.x)) / e.L - c.value);
      break;
    }
    // Angle between two separate edges: the directed angle from e1 to e2 is
    // `value`° (mod 180 — lines are undirected). sin(measured − target).
    case 'edgeAngle': {
      const a = E(c.edges[0]), b = E(c.edges[1]); const t = c.value * Math.PI / 180;
      const cr = a.dx * b.dy - a.dy * b.dx, dt = a.dx * b.dx + a.dy * b.dy;
      out.push((cr * Math.cos(t) - dt * Math.sin(t)) / (a.L * b.L));
      break;
    }
    // A rigid body: points[0] and points[1] are its frame (kept `value` mm apart),
    // and every further point keeps its frame coordinates `local[k] = [u, w]` (u
    // along points[0]→points[1], w along its left normal). Exactly 3 degrees of
    // freedom remain — a move and a turn — and, unlike a set of pairwise
    // distances, the body cannot flip over into its mirror image.
    case 'rigid': {
      const A = P(c.points[0]), B = P(c.points[1]);
      const dx = B.x - A.x, dy = B.y - A.y, L = Math.hypot(dx, dy) + EPS;
      out.push(L - c.value);
      const ux = dx / L, uy = dy / L;
      for (let k = 2; k < c.points.length; k++) {
        const q = P(c.points[k]); const [u, w] = c.local[k - 2];
        out.push(q.x - (A.x + ux * u - uy * w));
        out.push(q.y - (A.y + uy * u + ux * w));
      }
      break;
    }
    default: break;
  }
}

// `reg` weakly anchors every free point to its pre-solve position so the solve
// makes the SMALLEST move that satisfies the hard constraints ("minimal
// movement" — the essence of soft drag): undragged points stay put, and under-
// determined DOF don't wander. Hard constraints (weight 1) dominate the reg
// weight (~1e-3); the drag pull (~0.15) dominates reg on the grabbed point.
function residualFn(sketch, byId, idx, drag, reg) {
  // `drag` is one pull or a list of them (opts.pulls): each a soft target.
  const pulls = !drag ? [] : Array.isArray(drag) ? drag : [drag];
  const rw = reg ? reg.weight : 0;
  const aw = reg && reg.angleWeight ? reg.angleWeight : 0;
  return (params) => {
    const P = coordGetter(byId, idx, params);
    const out = [];
    for (const c of sketch.constraints) pushConstraintResiduals(out, c, P);
    for (const d of pulls) { const w = d.weight ?? 0.15; const p = P(d.point); out.push(w * (p.x - d.x)); out.push(w * (p.y - d.y)); }
    if (reg) for (const a of reg.anchors) { const p = P(a.id); out.push(rw * (p.x - a.x)); out.push(rw * (p.y - a.y)); }
    // Angle-preservation: penalise each edge ROTATING from its original direction
    // (sin of the turn). Weighted well above the position anchor, so the solve
    // absorbs a length edit by changing OTHER lengths (edges slide along their own
    // direction, unpenalised) and only bends an angle when it has no other choice.
    if (aw) for (const e of reg.angleEdges) {
      const a = P(e.aId), b = P(e.bId);
      const dx = b.x - a.x, dy = b.y - a.y, L = Math.hypot(dx, dy) + EPS;
      out.push(aw * (e.dx0 * dy - e.dy0 * dx) / L); // sin(Δθ) from the pre-solve direction
    }
    return out;
  };
}

// Forward-difference Jacobian of `residFn` at `params` (returns { J, r0 }).
function jacobian(params, residFn) {
  const r0 = residFn(params);
  const m = r0.length, n = params.length;
  const J = Array.from({ length: m }, () => new Float64Array(n));
  for (let j = 0; j < n; j++) {
    const save = params[j];
    const h = 1e-6 * (Math.abs(save) + 1);
    params[j] = save + h;
    const r1 = residFn(params);
    params[j] = save;
    for (let i = 0; i < m; i++) J[i][j] = (r1[i] - r0[i]) / h;
  }
  return { J, r0 };
}

// ---- Levenberg–Marquardt --------------------------------------------------

function lmSolve(params, residFn, opts = {}) {
  const maxIter = opts.maxIter ?? 80;
  const ftol = opts.ftol ?? 1e-16;
  const gtol = opts.gtol ?? 1e-12;
  let { J, r0 } = jacobian(params, residFn);
  let cost = dot(r0, r0);
  let lambda = 1e-3;
  const n = params.length;
  if (!n) return { cost };
  for (let iter = 0; iter < maxIter; iter++) {
    // Normal equations A = JᵀJ, g = Jᵀr.
    const A = Array.from({ length: n }, () => new Float64Array(n));
    const g = new Float64Array(n);
    for (let i = 0; i < J.length; i++) {
      const row = J[i], ri = r0[i];
      for (let a = 0; a < n; a++) {
        g[a] += row[a] * ri;
        const va = row[a];
        if (va === 0) continue;
        for (let b = a; b < n; b++) A[a][b] += va * row[b];
      }
    }
    for (let a = 0; a < n; a++) for (let b = 0; b < a; b++) A[a][b] = A[b][a];
    let gn = 0; for (let i = 0; i < n; i++) gn += g[i] * g[i];
    if (Math.sqrt(gn) < gtol) break;
    let improved = false;
    for (let tries = 0; tries < 14; tries++) {
      const Ad = A.map((row, i) => { const rr = Float64Array.from(row); rr[i] += lambda * (A[i][i] || 1); return rr; });
      const rhs = new Float64Array(n); for (let i = 0; i < n; i++) rhs[i] = -g[i];
      const delta = solveLinear(Ad, rhs);
      if (!delta) { lambda *= 4; continue; }
      const trial = Float64Array.from(params); for (let i = 0; i < n; i++) trial[i] += delta[i];
      const rt = residFn(trial);
      const ct = dot(rt, rt);
      if (ct < cost) {
        for (let i = 0; i < n; i++) params[i] = trial[i];
        cost = ct; ({ J, r0 } = jacobian(params, residFn));
        lambda = Math.max(lambda * 0.4, 1e-14); improved = true; break;
      }
      lambda *= 4;
    }
    if (!improved) break;
    if (cost < ftol) break;
  }
  return { cost };
}

// ---- null space (for per-entity DOF) --------------------------------------

// Basis of the null space of an m×n matrix (RREF; free columns → basis vectors).
function nullSpace(rows, n, tol = 1e-7) {
  const m = rows.length;
  const A = rows.map((r) => Float64Array.from(r));
  const pivots = [];
  let row = 0;
  for (let col = 0; col < n && row < m; col++) {
    let piv = -1, best = tol;
    for (let r = row; r < m; r++) { const v = Math.abs(A[r][col]); if (v > best) { best = v; piv = r; } }
    if (piv < 0) continue;
    const t = A[row]; A[row] = A[piv]; A[piv] = t;
    const d = A[row][col];
    for (let c = 0; c < n; c++) A[row][c] /= d;
    for (let r = 0; r < m; r++) { if (r === row) continue; const f = A[r][col]; if (!f) continue; for (let c = 0; c < n; c++) A[r][c] -= f * A[row][c]; }
    pivots.push({ row, col });
    row++;
  }
  const pivotSet = new Set(pivots.map((p) => p.col));
  const basis = [];
  for (let f = 0; f < n; f++) {
    if (pivotSet.has(f)) continue;
    const v = new Float64Array(n); v[f] = 1;
    for (const { row: r, col: c } of pivots) v[c] = -A[r][f];
    basis.push(v);
  }
  return basis;
}

// Which points are still under-defined, EXCLUDING rigid-body gauge motion. We
// stack the gauge modes (2 translations + rotation, when the sketch isn't
// anchored) onto the constraint Jacobian and take the null space: a point whose
// x or y appears in that null space can still move on its own → under-defined.
// Everything else is pinned (fully defined) and gets the "defined" colour.
function underdefinedPoints(sketch, points, byId, idx, params) {
  const n = params.length;
  if (!n) return new Set();
  const { J } = jacobian(Float64Array.from(params), residualFn(sketch, byId, idx, null));
  const rows = J.map((r) => Float64Array.from(r));
  if (!points.some((p) => p.fixed)) {
    let cx = 0, cy = 0, cnt = 0;
    for (const p of points) if (!p.fixed) { cx += p.x; cy += p.y; cnt++; }
    if (cnt) { cx /= cnt; cy /= cnt; }
    const tx = new Float64Array(n), ty = new Float64Array(n), rot = new Float64Array(n);
    for (const p of points) { const m = idx.get(p.id); if (!m) continue; tx[m.xi] = 1; ty[m.yi] = 1; rot[m.xi] = -(p.y - cy); rot[m.yi] = (p.x - cx); }
    rows.push(tx, ty);
    const absOrient = sketch.constraints.some((c) => c.type === 'horizontal' || c.type === 'vertical' || c.type === 'angle');
    if (!absOrient) rows.push(rot);
  }
  const basis = nullSpace(rows, n, 1e-7);
  const freeParam = new Array(n).fill(false);
  for (const v of basis) {
    let nrm = 0; for (let i = 0; i < n; i++) nrm += v[i] * v[i]; nrm = Math.sqrt(nrm) || 1;
    for (let i = 0; i < n; i++) if (Math.abs(v[i] / nrm) > 1e-4) freeParam[i] = true;
  }
  const free = new Set();
  for (const p of points) { const m = idx.get(p.id); if (m && (freeParam[m.xi] || freeParam[m.yi])) free.add(p.id); }
  return free;
}

// ---- gauge (rigid-body) freedoms ------------------------------------------

// Rigid-body freedoms the constraints do NOT remove: at most 2 translations + 1
// rotation. Computed from the null space rather than a type heuristic, so it is
// correct for every mix of absolute constraints — horizontal/vertical/angle
// (pin rotation), lockX/lockY and fixed points (pin translations, and jointly
// rotation). We work in the FULL parameter space (every point free, plus an
// explicit pin row per fixed coordinate) so that, e.g., a lone fixed point is
// correctly seen to still leave rotation about it free. Relative constraints
// (distance/parallel/perp/equal/collinear/pointOnLine/symmetric/…) leave all
// rigid freedoms. gauge = (independent rigid modes) − (modes the constraints
// remove).
function gaugeFreedoms(sketch, points) {
  const pts = points || [];
  if (!pts.length) return 0;
  const fidx = new Map(); let n = 0;
  for (const p of pts) { fidx.set(p.id, { xi: n, yi: n + 1 }); n += 2; }
  const byId = new Map(pts.map((p) => [p.id, p]));
  const params = new Float64Array(n);
  for (const p of pts) { const m = fidx.get(p.id); params[m.xi] = p.x; params[m.yi] = p.y; }
  const { J } = jacobian(params, residualFn(sketch, byId, fidx, null));
  const rows = J.map((r) => Float64Array.from(r));
  for (const p of pts) if (p.fixed) { // a fixed point pins both of its coordinates
    const m = fidx.get(p.id);
    const rx = new Float64Array(n); rx[m.xi] = 1; rows.push(rx);
    const ry = new Float64Array(n); ry[m.yi] = 1; rows.push(ry);
  }
  let cx = 0, cy = 0; for (const p of pts) { cx += p.x; cy += p.y; } cx /= pts.length; cy /= pts.length;
  const tx = new Float64Array(n), ty = new Float64Array(n), rot = new Float64Array(n);
  for (const p of pts) { const m = fidx.get(p.id); tx[m.xi] = 1; ty[m.yi] = 1; rot[m.xi] = -(p.y - cy); rot[m.yi] = (p.x - cx); }
  const modes = [tx, ty, rot];
  const modeDim = matrixRank(modes, 1e-9); // 3, or 2 for a single/all-coincident point (rot degenerate)
  // How many independent rigid modes the constraints remove: rank of J·modes.
  // Relative constraints are exactly invariant to rigid motion, so their J·mode
  // entries are 0 up to forward-difference noise (~1e-5 at mm scale); a genuine
  // removal (a lock → 1, an H/V/angle rotation coupling → an edge length) is
  // orders of magnitude larger. A relative tolerance keyed to the largest entry
  // separates the two robustly across sketch scales.
  const M = rows.map((row) => Float64Array.from([dot(row, tx), dot(row, ty), dot(row, rot)]));
  let mMax = 0; for (const r of M) for (const v of r) mMax = Math.max(mMax, Math.abs(v));
  const removed = M.length ? matrixRank(M, Math.max(1e-6, mMax * 1e-3)) : 0;
  return Math.max(0, modeDim - removed);
}

// A sketch is "conflicting" when the constraints cannot be jointly satisfied.
// Test the MEAN squared residual, not the raw sum: forward-difference precision
// leaves a tiny per-constraint error (~1e-3 mm), and summing it over hundreds of
// constraints would drift a perfectly-solved big sketch past an absolute
// threshold (false conflict). The mean is scale-invariant in constraint count,
// so the boundary stays put whether the sketch has 6 constraints or 600.
function isConflicting(r0, conflictTol) {
  const m = r0.length;
  if (!m) return false;
  return (dot(r0, r0) / m) > conflictTol;
}

// ---- public API -----------------------------------------------------------

// Solve `sketch` in place-safe fashion (returns new coords; never mutates the
// input). opts.drag = { point:id, x, y, weight? } adds a soft pull toward the
// cursor for interactive dragging; opts.pulls is a list of such pulls. opts.angleEdges = [[aId,bId],…] makes the
// solve PREFER keeping the angles between segments — it absorbs a constraint
// edit (e.g. a length change) into other segments' lengths and bends an angle
// only when the constraints leave no alternative. Returns:
//   { ok, points:[{id,x,y,fixed}], dof, gauge, shapeDefined, conflicting, residualNorm }
export function solveSketch(sketch, opts = {}) {
  const points = sketch.points || [];
  const byId = new Map(points.map((p) => [p.id, p]));
  const { idx, n } = buildParamIndex(points);
  const params = new Float64Array(n);
  for (const p of points) { const m = idx.get(p.id); if (m) { params[m.xi] = p.x; params[m.yi] = p.y; } }

  const drag = opts.drag ? { point: opts.drag.point, x: opts.drag.x, y: opts.drag.y, weight: opts.drag.weight ?? 0.15 } : null;
  // Regularise toward the CURRENT positions (captured before any warm-start) so
  // the solve moves as little as possible.
  const reg = { weight: opts.regWeight ?? 1e-3, anchors: points.filter((p) => !p.fixed).map((p) => ({ id: p.id, x: p.x, y: p.y })), angleWeight: 0, angleEdges: [] };
  // Angle-preservation (opts.angleEdges = list of [aId,bId] edges): prefer solving
  // a constraint edit by changing OTHER segments' lengths rather than the angles
  // between segments. The edge-rotation penalty sits well above a much weaker
  // position anchor, so length changes (sliding along an edge's own direction) are
  // nearly free while turning an edge is costly — angles bend only when forced.
  if (opts.angleEdges && opts.angleEdges.length) {
    reg.angleWeight = opts.angleWeight ?? 0.3;
    reg.weight = opts.regWeight ?? 1e-4; // weaken the position anchor so length changes stay cheap vs. angle changes
    reg.angleEdges = opts.angleEdges
      .filter(([a, b]) => byId.has(a) && byId.has(b))
      .map(([aId, bId]) => {
        const pa = byId.get(aId), pb = byId.get(bId);
        const dx = pb.x - pa.x, dy = pb.y - pa.y, L = Math.hypot(dx, dy) || 1;
        return { aId, bId, dx0: dx / L, dy0: dy / L };
      });
  }
  // Warm-start the dragged point at the cursor so the hard constraints pull the
  // rest of the sketch to the nearest satisfying configuration around it.
  if (drag) { const m = idx.get(drag.point); if (m) { params[m.xi] = drag.x; params[m.yi] = drag.y; } }
  // opts.pulls = [{ point, x, y, weight? }, …]: several soft targets at once (a
  // whole body pulled toward where an edit put it), without the warm start.
  const pulls = [...(drag ? [drag] : []), ...(opts.pulls || []).filter((q) => idx.has(q.point))];

  const fullResid = residualFn(sketch, byId, idx, pulls.length ? pulls : null, reg);
  lmSolve(params, fullResid, opts);

  // Break saddle stalls: the normalised sin/cos residuals (parallel/perpendicular/
  // angle) have a zero gradient when edges start exactly ±90° from their target
  // (e.g. "make these two perpendicular walls parallel"), so LM can't descend and
  // would misreport a satisfiable constraint as conflicting. If residual is still
  // high, retry from a few small DETERMINISTIC perturbations and keep the best —
  // genuinely conflicting sketches stay high through every restart.
  const conflictTol = opts.conflictTol ?? 1e-3;
  const hardResid = residualFn(sketch, byId, idx, null);
  const hardCostOf = (pp) => { const r = hardResid(pp); return dot(r, r); };
  const stalled = (pp) => isConflicting(hardResid(pp), conflictTol);
  if (n > 0 && stalled(params)) {
    const rng = mulberry32(0x51ec7ed);
    const restarts = opts.restarts ?? 4;
    let best = Float64Array.from(params), bestCost = hardCostOf(params);
    for (let k = 0; k < restarts && stalled(best); k++) {
      const trial = Float64Array.from(best);
      const amp = 0.5 * (k + 1); // grow the nudge (mm) if small ones don't dislodge it
      for (let i = 0; i < n; i++) trial[i] += (rng() - 0.5) * 2 * amp;
      lmSolve(trial, fullResid, opts);
      const c = hardCostOf(trial);
      if (c < bestCost) { best = trial; bestCost = c; }
    }
    params.set(best);
  }

  // A caller that only wants the solved positions (a live solve on every mouse
  // move) skips the DOF analysis below — it re-differentiates the whole sketch.
  if (opts.diagnostics === false) {
    const r = hardResid(params);
    return {
      ok: true,
      points: points.map((p) => { const m = idx.get(p.id); return m ? { id: p.id, x: params[m.xi], y: params[m.yi], fixed: !!p.fixed, free: false } : { id: p.id, x: p.x, y: p.y, fixed: true, free: false }; }),
      dof: NaN, gauge: NaN, shapeDefined: false, conflicting: isConflicting(r, conflictTol), residualNorm: Math.sqrt(dot(r, r)),
    };
  }

  // Per-point under-defined flags (gauge-excluded) for per-entity colouring.
  const free = underdefinedPoints(sketch, points, byId, idx, params);
  const outPoints = points.map((p) => {
    const m = idx.get(p.id);
    return m ? { id: p.id, x: params[m.xi], y: params[m.yi], fixed: !!p.fixed, free: free.has(p.id) }
      : { id: p.id, x: p.x, y: p.y, fixed: true, free: false };
  });

  // DOF / conflict diagnostics from the HARD constraints only (no drag pull).
  const hard = jacobian(Float64Array.from(params), residualFn(sketch, byId, idx, null));
  const rank = matrixRank(hard.J, 1e-7);
  const dof = n - rank;
  const gauge = gaugeFreedoms(sketch, points);
  const hardCost = dot(hard.r0, hard.r0);
  const conflicting = isConflicting(hard.r0, opts.conflictTol ?? 1e-3);
  const shapeDefined = (dof - gauge) <= 0 && !conflicting;
  return { ok: true, points: outPoints, dof, gauge, shapeDefined, conflicting, residualNorm: Math.sqrt(hardCost) };
}

// DOF diagnostics for the sketch AS-IS (no re-solve, no move). Useful for a
// live "fully defined / N DOF / conflicting" readout after each edit.
export function analyzeSketch(sketch) {
  const points = sketch.points || [];
  const byId = new Map(points.map((p) => [p.id, p]));
  const { idx, n } = buildParamIndex(points);
  const params = new Float64Array(n);
  for (const p of points) { const m = idx.get(p.id); if (m) { params[m.xi] = p.x; params[m.yi] = p.y; } }
  const hard = jacobian(params, residualFn(sketch, byId, idx, null));
  const rank = matrixRank(hard.J, 1e-7);
  const dof = n - rank;
  const gauge = gaugeFreedoms(sketch, points);
  const hardCost = dot(hard.r0, hard.r0);
  const conflicting = isConflicting(hard.r0, 1e-3);
  return { dof, gauge, shapeDefined: (dof - gauge) <= 0 && !conflicting, conflicting, residualNorm: Math.sqrt(hardCost) };
}

// Would adding `constraint` over-constrain the sketch (make it inconsistent or
// redundant)? Returns { conflicting, redundant } by comparing ranks with and
// without it. Used to warn before committing a relational constraint.
export function wouldConflict(sketch, constraint) {
  const before = analyzeSketch(sketch);
  const trial = { points: sketch.points, constraints: [...sketch.constraints, constraint] };
  const after = solveSketch(trial);
  const redundant = !after.conflicting && after.dof === before.dof; // added no new restriction
  return { conflicting: after.conflicting, redundant };
}
